import {isEmail} from '@ivosabev/helpers/isEmail';
import {toNumber} from '@ivosabev/helpers/toNumber';
import {render} from '@react-email/components';
import {type MetaFunction, json} from '@remix-run/node';
import {useFetcher} from '@remix-run/react';
import {clsx} from 'clsx';
import {eq} from 'drizzle-orm';

import type Mail from 'nodemailer/lib/mailer';
import hashize from 'object-hash';
import {type ChangeEvent, type MutableRefObject, useRef, useState} from 'react';
import {Button} from '#/app/components/Button.jsx';
import {Container} from '#/app/components/Container.jsx';
import {SavingsBreakdownEmail} from '#/app/components/emails/SavingsBreakdownEmail.jsx';
import {FAQ} from '#/app/components/FAQ.jsx';
import {Image} from '#/app/components/Image.jsx';
import {BASE_URL} from '#/app/constants.js';
import {CalculatorFAQ as FAQs} from '#/app/content.js';
import {calcSavings} from '#/app/helpers/calcSavings.js';
import {createMetaHandler} from '#/app/helpers/createMetaHandler.js';
import {createMetaTags} from '#/app/helpers/createMetaTags.js';
import * as gtag from '#/app/helpers/gtag.client.js';
import {sendEmail} from '#/app/helpers/sendEmail.js';
import {calculations} from '#/app/schema.js';
import {db} from '#/db/db.server.js';

const isValidNumberInput = (v: string) => {
  const vn = toNumber(v);
  return String(v).length === 0 || (String(v).length > 0 && !Number.isNaN(vn) && vn > 0);
};

const getEmailCallToAction = ({isFormFilled, numberOfDeliveries, numberOfVehicles, savings}: Record<string, any>) => {
  const ratio = toNumber(numberOfDeliveries) / toNumber(numberOfVehicles);
  if (isFormFilled && (toNumber(numberOfVehicles) > 1000 || ratio < 150)) {
    return 'special';
  } else if (isFormFilled && toNumber(savings) < 300) {
    return 'wizard';
  } else if (isFormFilled && toNumber(numberOfVehicles) > 50) {
    return 'enterprise';
  } else {
    return 'normal';
  }
};

export const meta: MetaFunction = createMetaHandler(({location}) => {
  return [
    ...createMetaTags({
      description:
        'Optimize your last mile delivery costs. Find out how much fuel, CO2 emissions, and work hours you can save with Ufleet.',
      title: 'Last mile costs optimization calculator - Ufleet',
      url: `https://ufleet.io${location.pathname}`,
    }),
  ];
});

const POSITIVE = [
  'Great customer experience',
  'Brand differentiator',
  'Customer loyalty',
  'Reduced delivery costs',
  'Better operational efficiency',
  'Reduced environmental impact',
  'Data-driven delivery process  ',
];

const NEGATIVE = [
  'Poor customer experience',
  'Lack of visibility on deliveries',
  'Failed deliveries',
  'High labor costs',
  'High fuel consumption',
  'Lots of carbon emissions',
  'Lots of idle time',
];

function CalculateSection({vehiclesInputRef}: {vehiclesInputRef: React.RefObject<HTMLInputElement>}) {
  return (
    <div className="mt-16 space-y-8 text-center">
      <p className="mx-auto mt-16 w-full whitespace-pre-wrap text-center text-2xl font-medium leading-relaxed text-gray-700 sm:text-2xl lg:w-4/5">
        Calculate cost savings with
        {' '}
        <em>Ufleet</em>
        {' '}
        route optimization software
      </p>
      <Button
        appearance="primary"
        className="_cta"
        color="sky"
        href="#top"
        onClick={() => {
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
          vehiclesInputRef.current?.focus();
        }}
        size="lg"
      >
        Calculate
      </Button>
    </div>
  );
}

export async function action({request}: UFActionArgs) {
  const body = await request.formData();
  const data = Object.fromEntries(body.entries()) as {email: string; numberOfDeliveries: string; numberOfVehicles: string};

  const savings = calcSavings(Number(data.numberOfDeliveries), Number(data.numberOfVehicles)).monthlySavings;
  const emailCallToAction = getEmailCallToAction({...data, isFormFilled: true, savings});

  const hash = hashize(data);
  const summary = hash ? (await db.select().from(calculations).where(eq(calculations.hash, hash)).limit(1)).pop() : undefined;
  if (!summary) {
    // @ts-expect-error
    await db.insert(calculations).values({
      ...data,
      hash,
      metadata: {headers: Object.fromEntries(request.headers.entries())},
      type: emailCallToAction,
    });
  } else {
    await db.update(calculations).set({updatedAt: new Date()}).where(eq(calculations.id, summary.id));
  }

  const externalMessage: Mail.Options = {
    html: await render(<SavingsBreakdownEmail
      email={data.email}
      hash={hash}
      type={emailCallToAction}
    />),
    subject: '',
    to: data.email,
  };
  const internalMessage: Mail.Options = {
    html: await render(<div>
      <a
        href={`${BASE_URL}/calculator/summary/${hash}`}
        rel="noreferrer"
        target="_blank"
      >
        Summary for {data.email}
      </a>
    </div>),
    subject: '',
    to: 'contact@ufleet.io',
  };
  if (emailCallToAction === 'normal') {
    externalMessage.subject = `Your last mile savings breakdown by Ufleet`;
    internalMessage.subject = 'Ufleet | Savings Calculator - New Submission';
  } else {
    externalMessage.subject = `Let's talk about your last mile savings by Ufleet!`;
    internalMessage.replyTo = data.email;
    internalMessage.subject = `Ufleet | Savings Calculator - Special Case (${emailCallToAction}) - ACTION REQUIRED`;
  }

  try {
    const result = await sendEmail(externalMessage);
    console.log('R', result);
    await sendEmail(internalMessage);

    return json({status: 'ok'});
  } catch (e) {
    console.log('E', e);
    return json({errors: e, status: 'error'}, 500);
  }
}

const getInputStyles = (hasErrors: boolean) =>
  clsx(
    'block w-full min-w-0 flex-1 rounded-md border border-gray-200 px-3.5 py-2 text-gray-700 placeholder:text-gray-300 sm:text-lg',
    hasErrors ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-500 focus:border-sky-500 focus:ring-sky-500',
  );

const initialFormValues = {
  email: '',
  numberOfDeliveries: '',
  numberOfVehicles: '',
  // email: 'ivolution@gmail.com',
  // numberOfDeliveries: '2',
  // numberOfVehicles: '1',
};

const Calculator = () => {
  const fetcher = useFetcher();

  const [isFormDirty, setIsFormDirty] = useState(true);
  const [isFormStarted, setIsFormStarted] = useState(false);
  const [data, setData] = useState(initialFormValues);
  const [errors, setErrors] = useState<Record<string, any>>({
    email: '', // 'Please enter a valid email address',
    numberOfDeliveries: '', // 'Please enter a number',
    numberOfVehicles: '', // 'Please enter a number',
  });
  const hasErrors = Object.values(errors).some((v) => !!v);
  const DEFAULT_SAVINGS = '?';
  const [savings, setSavings] = useState<'?' | number>(DEFAULT_SAVINGS);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isFormStarted) {
      setIsFormStarted(true);
      gtag.event({action: 'form_start', form_id: 'calculator'});
    }

    const newData = {...data, [e.target.name]: e.target.value};
    setData(newData);

    const _errors = {} as Record<string, any>;
    if (String(newData.numberOfDeliveries).length || String(newData.numberOfVehicles).length) {
      _errors.numberOfDeliveries = isValidNumberInput(newData.numberOfDeliveries) ? undefined : 'Please enter a valid number';
      _errors.numberOfVehicles = isValidNumberInput(newData.numberOfVehicles) ? undefined : 'Please enter a valid number';
      if (_errors.numberOfDeliveries || _errors.numberOfVehicles) {
        setSavings(DEFAULT_SAVINGS);
      } else if (String(newData.numberOfDeliveries).length && String(newData.numberOfVehicles).length) {
        const savings = calcSavings(Number(newData.numberOfDeliveries), Number(newData.numberOfVehicles));
        setSavings(savings.monthlySavings);
      }
    } else {
      setSavings(DEFAULT_SAVINGS);
    }

    setIsFormDirty(true);

    _errors.email = (newData.email || newData.email === '') && !isEmail(newData.email) && 'Please enter a valid email address';
    setErrors(_errors);
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (hasErrors) {
      gtag.event({action: 'form_error', errors: JSON.stringify(errors), form_id: 'calculator'});
    } else if (!isSubmitting && !isSubmitted) {
      fetcher.submit((e.target as HTMLAnchorElement).closest('form'));
      gtag.event({action: 'form_submit', form_id: 'calculator'});
      setIsFormDirty(false);
      setIsFormStarted(false);
      setData(initialFormValues);
    }
  };

  const isSubmitting = fetcher.state === 'submitting';
  const isSubmitted = fetcher.state === 'idle' && fetcher.data && !isFormDirty;
  const isFormFilled = typeof savings === 'number';

  const emailCallToAction = getEmailCallToAction({...data, isFormFilled, savings});

  const vehiclesInputRef = useRef() as MutableRefObject<HTMLInputElement>;

  return (
    <>
      <fetcher.Form id="calculator" method="post">
        <Container className="px-2 py-8">
          <div className="flex flex-col items-center justify-between py-8 lg:flex-row-reverse">
            <div className="max-w-xl xl:w-6/12">
              <div className="mb-12 space-y-8">
                <h2 className="whitespace-pre-wrap">Optimize your delivery costs with Ufleet</h2>
                <p className="ml-0.5 whitespace-pre-wrap text-xl leading-relaxed text-gray-500 sm:text-2xl">
                  Reduce last mile costs. Find out how much you can save with
                  {' '}
                  <span className="font-medium text-sky-500">Ufleet</span>
                  {' '}
                  route
                  optimization software.
                </p>
                <p className="ml-0.5 whitespace-pre-wrap pr-4 text-xl leading-relaxed text-gray-500 sm:text-2xl">
                  Get a complete breakdown of all potential savings such as:
                </p>
                <ul className="ml-10 list-disc space-y-2 whitespace-pre-wrap text-xl leading-relaxed text-gray-500 sm:text-2xl">
                  <li className="pl-4">Total delivery costs</li>
                  <li className="pl-4">Deivery drivers&apos; time</li>
                  <li className="pl-4">Office workers&apos; time</li>
                  <li className="pl-4">Fuel consumption</li>
                  <li className="pl-4">CO₂ emissions</li>
                </ul>
              </div>
            </div>
            <div className="mr-4 flex w-full flex-row-reverse items-center justify-end sm:w-2/3 md:w-[630px]">
              <div className="flex-1 space-y-10 rounded-3xl border border-gray-200 bg-gradient-to-tl from-gray-100 to-gray-50 px-8  py-10 shadow-xl xl:px-12">
                <div className="pb-4 text-center text-3xl font-semibold text-gray-700">
                  Last mile cost
                  <br />
                  optimization calculator
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="ml-1 block text-lg font-medium text-gray-700" htmlFor="numberOfVehicles">
                      Number of Vehicles
                    </label>
                    <div className="flex rounded-md shadow-sm">
                      <input
                        className={getInputStyles(!!errors.numberOfVehicles)}
                        name="numberOfVehicles"
                        onChange={handleChange}
                        placeholder=""
                        ref={vehiclesInputRef}
                        type="text"
                        value={data.numberOfVehicles}
                      />
                    </div>
                    {errors.numberOfVehicles && <div className="py-1 text-red-500">{errors.numberOfVehicles}</div>}
                  </div>
                  <div>
                    <label className="ml-1 block text-lg font-medium text-gray-700" htmlFor="numberOfDeliveries">
                      Deliveries per Month
                    </label>
                    <div className="mt-2 flex rounded-md shadow-sm">
                      <input
                        className={getInputStyles(!!errors.numberOfDeliveries)}
                        name="numberOfDeliveries"
                        onChange={handleChange}
                        placeholder=""
                        type="text"
                        value={data.numberOfDeliveries}
                      />
                    </div>
                    {errors.numberOfDeliveries && <div className="py-1 text-red-500">{errors.numberOfDeliveries}</div>}
                  </div>
                </div>
                {emailCallToAction === 'normal' && (
                  <div className="flex flex-col items-center justify-center">
                    <div className="text-xl font-medium text-gray-700">Your potential savings are</div>
                    <div className="-ml-4 flex items-start text-[5rem] font-semibold text-sky-500">
                      {savings !== DEFAULT_SAVINGS && <span className="mr-1 mt-[1.7rem] text-4xl font-semibold">$</span>}
                      {savings}
                    </div>
                  </div>
                )}
                {emailCallToAction === 'wizard' && (
                  <div className="mx-2 text-center text-xl text-gray-700">
                    You are an operational Wizzard,
                    <br />
                    even without
                    {' '}
                    <span className="font-medium text-sky-500">Ufleet</span>
                    . Please leave your email. Maybe we can learn
                    something from you!
                  </div>
                )}
                {emailCallToAction === 'special' && (
                  <div className="mx-2 text-center text-xl text-gray-700">
                    It seems that your delivery operation is quite special. Get in touch for a free delivery audit and consultation.
                  </div>
                )}
                {emailCallToAction === 'enterprise' && (
                  <div className="mx-2 text-center text-xl text-gray-700">
                    It seems that you are running an enterprise grade delivery operation. Please leave your contacts and our founder will
                    personally get in touch with you.
                  </div>
                )}
                {isFormFilled ? (
                  <div className="space-y-4 text-center">
                    <div>
                      <div className="mt-2 flex rounded-md shadow-sm">
                        <input
                          className={getInputStyles(!!errors.errors)}
                          name="email"
                          onChange={handleChange}
                          placeholder={emailCallToAction === 'normal' ? 'Leave email for full breakdown' : 'Leave us your email'}
                          type="text"
                          value={data.email}
                        />
                      </div>
                      {errors.email && <div className="py-1 text-red-500">{errors.email}</div>}
                      {isSubmitted && <div className="my-2 font-medium text-green-600">You will get your breakdown shortly!</div>}
                    </div>
                    <Button
                      appearance="primary"
                      className="w-full"
                      color="sky"
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                      size="lg"
                    >
                      {emailCallToAction === 'normal' ? 'Send Me Savings Breakdown' : `Let's get in touch`}
                    </Button>
                  </div>
                ) : (
                  <div className="flex h-32 items-center justify-center text-center text-gray-700">
                    Use the last mile delivery costs calculator to see how much you can save with Ufleet.
                  </div>
                )}
              </div>
              <div className="hidden w-[200px] justify-end md:flex">
                <Image
                  alt="a guy leaning on the ufleet savings calculator"
                  className=""
                  src="/savings-calculator.png"
                />
              </div>
            </div>
          </div>
        </Container>
      </fetcher.Form>
      <Container className="my-32 space-y-32">
        <div className="">
          <h2 className="text-center">Understanding the last mile problem</h2>
          <div className="mx-auto mt-16 w-full space-y-8 text-xl leading-relaxed text-gray-500 sm:text-2xl lg:w-4/5">
            <p className="whitespace-pre-wrap text-center">
              Last mile delivery represents the final and most crucial leg in the delivery process. It's responsible for the majority of the
              delivery costs – often more than 50% of total expenses.
            </p>
            <p className="whitespace-pre-wrap text-center">
              This stage faces numerous challenges such as urban congestion, multiple stops, difficulty with parking, more scratches and
              dents on vehicles, etc. While these are factors that you cannot fully control, there are others that you can.
            </p>
            <p className="whitespace-pre-wrap text-center">
              Depending on how you manage your last mile delivery process you can either enjoy the benefits or suffer the negatives.
            </p>
          </div>
          <div className="mx-auto my-16 flex w-full flex-col space-y-4 text-xl sm:flex-row sm:space-x-4 sm:space-y-0 lg:w-3/4 lg:text-2xl">
            <div className="w-full space-y-2 sm:w-1/2 md:space-y-4 lg:px-8">
              <div className="text-center text-3xl font-semibold text-gray-700">Negatives</div>
              {NEGATIVE.map((title) => (
                <div
                  className="flex items-center justify-center rounded-xl bg-red-100 p-4 text-center text-red-800 transition ease-in hover:bg-red-200"
                  key={title}
                >
                  {title}
                </div>
              ))}
            </div>
            <div className="w-full space-y-2 sm:w-1/2 md:space-y-4 lg:px-8">
              <div className="text-center text-3xl font-semibold text-gray-700">Benefits</div>
              {POSITIVE.map((title) => (
                <div
                  className="flex items-center justify-center rounded-xl bg-green-100 p-4 text-center text-green-800 transition ease-in hover:bg-green-200"
                  key={title}
                >
                  {title}
                </div>
              ))}
            </div>
            {/* // <div className="grid grid-cols-3 gap-4 mx-0 sm:mx-48 mt-16">
          //     <div
          //       className={`transition ease-in h-48 rounded-xl text-2xl p-16 text-center flex items-center justify-center  ${type === 0 ? 'bg-red-100 hover:bg-red-200 text-red-800' : 'bg-green-100 hover:bg-green-200 text-green-800'}`}
          //       key={label}
          //     >
          //       {label}
          //     </div>
          // </div> */}
          </div>
          <CalculateSection vehiclesInputRef={vehiclesInputRef} />
        </div>
        <div className="">
          <FAQ content={FAQs} />
          <CalculateSection vehiclesInputRef={vehiclesInputRef} />
        </div>
        <div className="">
          <h2 className="text-center">
            What you get with
            {' '}
            <em>Ufleet</em>
          </h2>
          <div className="my-16 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-xl bg-gray-50 p-8 transition ease-in hover:bg-gray-100">
              <div className={`mb-4 text-lg font-semibold uppercase text-emerald-500`}>Efficiency</div>
              <div className="space-y-8">
                <div className="flex h-14 items-center">
                  <h4 className="font-semibold">
                    Cost
                    {' '}
                    <span className="text-emerald-400x">efficient</span>
                    <br />
                    delivery
                  </h4>
                </div>
                <p className="text-xl text-gray-700">
                  Find the shortest, fastest, and most efficient delivery routes with a few clicks. Turn hours of work into minutes.
                </p>
                <ul className="ml-4 list-outside list-disc space-y-2 text-lg text-gray-700">
                  <li>Reduce mileage</li>
                  <li>Cut down on idle time</li>
                  <li>Consume 20% less fuel</li>
                  <li>Lower CO2 emissions</li>
                  <li>Empower staff to do more with less</li>
                </ul>
              </div>
            </div>
            <div className="rounded-xl bg-gray-50 p-8 transition ease-in hover:bg-gray-100">
              <div className={`mb-4 text-lg font-semibold uppercase text-sky-500`}>Technology</div>
              <div className="space-y-8">
                <div className="flex h-14 items-center">
                  <h4 className="font-semibold">
                    <span className="text-sky-400x">Technology</span>
                    {' '}
                    driven operations
                  </h4>
                </div>
                <p className="text-xl text-gray-700">
                  Ditch the pen and paper and future-proof your deliveries. Run lean and predictable operations, and empower your team.
                </p>
                <ul className="ml-4 list-outside list-disc space-y-2 text-lg text-gray-700">
                  <li>Plan thousands of deliveries in the blink of an eye</li>
                  <li>Re-optimize and reschedule in real-time</li>
                  <li>Empower drivers with an easy-to-use mobile app</li>
                  <li>Collect proof of delivery photos and notes</li>
                  <li>Analyze performance and make data-driven decisions</li>
                </ul>
              </div>
            </div>
            <div className="rounded-xl bg-gray-50 p-8 transition ease-in hover:bg-gray-100">
              <div className={`mb-4 text-lg font-semibold uppercase text-pink-500`}>Experience</div>
              <div className="space-y-8">
                <div className="flex h-14 items-center">
                  <h4 className="font-semibold">
                    Stellar delivery
                    {' '}
                    <span className="text-pink-500x">experience</span>
                  </h4>
                </div>
                <p className="text-xl text-gray-700">
                  Impress your customers with a convenient and modern delivery experience. Build lasting customer loyalty.
                </p>
                <ul className="ml-4 list-outside list-disc space-y-2 text-lg text-gray-700">
                  <li>Live tracking and notifications</li>
                  <li>Customer feedback upon delivery</li>
                  <li>Track delays or missed deliveries</li>
                  <li>Promote loyalty programs and more</li>
                  <li>Enable seamless communication between drivers and customers</li>
                </ul>
              </div>
            </div>
          </div>
          <CalculateSection vehiclesInputRef={vehiclesInputRef} />
        </div>
      </Container>
    </>
  );
};

export default Calculator;
